/** @jsxImportSource @emotion/react */
import { css, useTheme } from "@emotion/react";
import { TableHead, TableRow, TableHeader } from '@coreai/component-library';
import { useState } from "react";

type SortDirectionType = 'asc' | 'desc' | null;

export interface OnSortProps {
  key: string;
  direction: SortDirectionType;
};

export interface THeaderProps {
  key: string;
  width: string;
  sortDirection: SortDirectionType;
  onSort: ({ key, direction }: OnSortProps) => void;
  isSortable: boolean;
  className?: string;
  title: string | JSX.Element;
  isHidden?: boolean;
  renderRowContent?: (rowData: any) => JSX.Element;
  tdCellClassName?: string;
}

const sortableActionsStyles = css`
  display: flex;
  justify-content: left;
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

function SortActionsItems({
  header,
  activeSortKey,
  currentSortDirection,
  setSorting
}: { 
  header: THeaderProps;
  activeSortKey: string | null;
  currentSortDirection: SortDirectionType;
  setSorting: (key: string) => void;
}) {
  const theme = useTheme();

  const iconStyles = (sortDirection: SortDirectionType) => css`
    position: relative;
    width: 20px;
    height: 20px;

    &::before,
    &::after {
      position: absolute;
      font-size: 16px;
      transition: color 0.3s ease-in-out;
      display: block;
      line-height: 1;
    }

    &::before {
      content: '⭡';
      top: 0;
      left: 2px;
      color: ${sortDirection === 'desc' ? theme.colors.primary : theme.colors.strokeGray};
    }

    &::after {
      content: '⭣';
      bottom: 0;
      right: 2px;
      color: ${sortDirection === 'asc' ? theme.colors.primary : theme.colors.strokeGray};
    }
  `;

  return (
    <div css={sortableActionsStyles} onClick={() => setSorting(header.key)}>
      <div css={iconStyles(activeSortKey === header.key ? currentSortDirection : null)} />
    </div>
  );
}

const theadStyles = (width: string) => css`
  position: relative;
  min-width: ${width};
  width: ${width};
  padding-right: 0px;
`;

const colActionsStyles = css`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

function THeaders({ headers }: { headers: THeaderProps[] }) {
  const [activeSortKey, setActiveSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(null);

  const setSorting = (columnKey: string) => {
    let newSortDirection: SortDirectionType = null;

    if (activeSortKey !== columnKey) {
      setActiveSortKey(columnKey);
      newSortDirection = 'asc';
    } else {
      if (sortDirection === null) {
        newSortDirection = 'asc';
      } else if (sortDirection === 'asc') {
        newSortDirection = 'desc';
      } else {
        newSortDirection = null;
        setActiveSortKey(null);
      }
    }

    setSortDirection(newSortDirection);
    
    const header = headers.find(h => h.key === columnKey);
    if (header) {
      header.onSort({ key: columnKey, direction: newSortDirection });
    }
  };

  return (
    <TableHeader>
      <TableRow>
        {headers.map(header => (
          <TableHead key={header.key} css={theadStyles(header?.width)} className={header?.className}>
            {header?.title}
            <div css={colActionsStyles}>
              {header.isSortable && (
                <SortActionsItems
                  header={header}
                  activeSortKey={activeSortKey}
                  currentSortDirection={sortDirection}
                  setSorting={setSorting}
                />
              )}
            </div>
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  );
};

THeaders.defaultProps = {
  isHidden: false,
  className: '',
  tdCellClassName: '',
  sortDirection: null,
  onSort: () => {},
  isSortable: false,
  renderRowContent: () => {},
  title: '',
};

export default THeaders;